import { useEffect, useRef } from 'react'
import { twMerge } from 'tailwind-merge'

interface CellProgressProps {
  /** Duration in milliseconds */
  duration: number
  /** Start time as ISO string or Date */
  startTime: string | Date
  /** Callback when progress completes */
  onComplete?: () => void
  className?: string
}

function CellProgress({ duration, startTime, onComplete, className }: CellProgressProps) {
  const onCompleteRef = useRef(onComplete)
  onCompleteRef.current = onComplete
  const indicatorRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    const el = indicatorRef.current
    if (!el) return

    const startMs = new Date(startTime).getTime()
    const elapsed = Date.now() - startMs
    const remaining = Math.max(duration - elapsed, 0)
    const initialScale = Math.max(1 - elapsed / duration, 0)

    if (remaining <= 0) {
      el.style.transform = 'scaleX(0)'
      onCompleteRef.current?.()
      return
    }

    // Set initial position without transition
    el.style.transitionDuration = '0ms'
    el.style.transform = `scaleX(${initialScale})`

    // Kick off the animation on a later frame so the initial state is painted
    // first. Using rAF instead of a synchronous getBoundingClientRect() avoids
    // forcing a layout reflow per progress bar (which thrashes when several
    // cells enter the pending state at once). The transition itself stays a
    // single GPU-composited transform.
    let innerRaf = 0
    const outerRaf = requestAnimationFrame(() => {
      innerRaf = requestAnimationFrame(() => {
        el.style.transitionDuration = `${remaining}ms`
        el.style.transform = 'scaleX(0)'
      })
    })

    const id = setTimeout(() => onCompleteRef.current?.(), remaining)
    return () => {
      cancelAnimationFrame(outerRaf)
      cancelAnimationFrame(innerRaf)
      clearTimeout(id)
    }
  }, [duration, startTime])

  return (
    <div className="corner-smooth pointer-events-none absolute inset-0 overflow-hidden rounded-2xl">
      <div className={twMerge('absolute bottom-0 left-0 h-1 w-full', className)}>
        <div
          ref={indicatorRef}
          className="bg-success h-full origin-left"
          style={{
            transitionProperty: 'transform',
            transitionTimingFunction: 'linear',
          }}
        />
      </div>
    </div>
  )
}

export { CellProgress }
